import { useMemo, type CSSProperties } from 'react';
import { type Month, WEEKDAYS, monthGrid, shiftMonth, thisMonth, today } from '../dates';
import { type Workout, volumeOf } from '../types';
import { Icon } from './Icon';

type Props = {
  month: Month;
  onMonthChange: (m: Month) => void;
  workouts: Map<string, Workout>;
  selected: string | null;
  onSelect: (date: string) => void;
};

export function Calendar({ month, onMonthChange, workouts, selected, onSelect }: Props) {
  const cells = useMemo(() => monthGrid(month), [month]);
  const todayKey = today();

  const { heat, stats } = useMemo(() => {
    const volumes = new Map<string, number>();
    let sets = 0;
    let days = 0;

    for (const cell of cells) {
      if (!cell.inMonth) continue;
      const w = workouts.get(cell.key);
      if (!w || w.entries.length === 0) continue;
      days += 1;
      sets += w.entries.reduce((s, e) => s + e.sets, 0);
      volumes.set(cell.key, volumeOf(w.entries));
    }

    const total = [...volumes.values()].reduce((s, v) => s + v, 0);
    const max = Math.max(1, ...volumes.values());
    // 平方根で圧縮すると、軽い日も潰れずに濃淡が読める
    const heat = new Map([...volumes].map(([k, v]) => [k, Math.sqrt(v / max)]));

    return { heat, stats: { days, sets, total } };
  }, [cells, workouts]);

  const current = thisMonth();
  const atCurrentMonth = month.year * 100 + month.month >= current.year * 100 + current.month;

  return (
    <>
      <div className="monthbar">
        <div className="monthbar__title">
          <span className="monthbar__year mono">{month.year}</span>
          <span className="monthbar__month">
            {String(month.month).padStart(2, '0')}
            <sup>月</sup>
          </span>
        </div>
        <div className="monthbar__nav">
          <button className="iconbtn" onClick={() => onMonthChange(shiftMonth(month, -1))} aria-label="前の月">
            <Icon name="chevronLeft" />
          </button>
          <button
            className="iconbtn"
            onClick={() => onMonthChange(shiftMonth(month, 1))}
            aria-label="次の月"
            disabled={atCurrentMonth}
            style={atCurrentMonth ? { opacity: 0.3, cursor: 'not-allowed' } : undefined}
          >
            <Icon name="chevronRight" />
          </button>
        </div>
      </div>

      <div className="stats">
        <div className="stat">
          <div className="label">実施日数</div>
          <div className="stat__value">
            {stats.days}
            <small>日</small>
          </div>
        </div>
        <div className="stat">
          <div className="label">総セット</div>
          <div className="stat__value">
            {stats.sets}
            <small>set</small>
          </div>
        </div>
        <div className="stat">
          <div className="label">総ボリューム</div>
          <div className="stat__value">
            {stats.total >= 10000
              ? (stats.total / 1000).toFixed(1)
              : Math.round(stats.total).toLocaleString('ja-JP')}
            <small>{stats.total >= 10000 ? 't' : 'kg'}</small>
          </div>
        </div>
      </div>

      <div className="calendar">
        <div className="calendar__head">
          {WEEKDAYS.map((wd, i) => (
            <div
              key={wd}
              className={`calendar__wd${i === 0 ? ' calendar__wd--sun' : i === 6 ? ' calendar__wd--sat' : ''}`}
            >
              {wd}
            </div>
          ))}
        </div>

        <div className="calendar__grid">
          {cells.map((cell, i) => {
            const h = heat.get(cell.key);
            const trained = h !== undefined;
            const classes = [
              'day',
              trained ? 'day--trained' : '',
              cell.inMonth ? '' : 'day--out',
              cell.key === todayKey ? 'day--today' : '',
              cell.key === selected ? 'day--selected' : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <button
                key={cell.key}
                className={classes}
                style={{ '--i': String(i), '--heat': String(h ?? 0) } as CSSProperties}
                onClick={() => onSelect(cell.key)}
                aria-label={`${cell.key}${trained ? ' トレーニング記録あり' : ''}`}
              >
                <span className="day__num">{Number(cell.key.slice(8))}</span>
                {trained && <span className="day__bar" />}
              </button>
            );
          })}
        </div>

        <div className="legend">
          <span className="label">軽い</span>
          {[14, 34, 56, 80].map((sw) => (
            <span key={sw} className="legend__swatch" style={{ '--sw': `${sw}%` } as CSSProperties} />
          ))}
          <span className="label">重い</span>
        </div>
      </div>
    </>
  );
}
